import React, { memo, forwardRef, useMemo } from 'react';
import { Box, useTheme, alpha } from "@mui/material";
import { useSelector } from 'react-redux';

/**
 * Section Component with variants for common patterns
 * 
 * @param {string} component - HTML element (default: "section")
 * @param {string} variant - Background variant: 'default' | 'paper' | 'light' | 'white' | 'dark' | 'primary' | 'success' | 'warning' | 'error' | 'info'
 * @param {string|object} padding - Padding variant: 'none' | 'small' | 'medium' | 'large' | custom spacing
 * @param {boolean} fullHeight - Full viewport height
 * @param {string} type - Section type: 'header' | undefined (header type applies header-specific styles)
 * @param {object} sx - Additional sx styles
 * @param {ReactNode} children - Section content
 */
export const Section = memo(forwardRef(({
    component = "section",
    variant = 'default',
    padding = 'medium',
    fullHeight = false,
    type,
    children,
    sx,
    ...props
}, ref) => {
    const theme = useTheme();
    const config = useSelector((store) => store.app);
    // Background variants - memoized
    const backgroundVariants = useMemo(() => ({
        default: theme.palette.background.default,
        paper: theme.palette.background.paper,
        light: theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[50],
        white: theme.palette.mode === 'dark' ? theme.palette.grey[900] : theme.palette.common.white,
        dark: theme.palette.mode === 'dark' ? theme.palette.grey[900] : theme.palette.grey[900],
        primary: theme.palette.primary.main,
        success: theme.palette.mode === 'dark' ? theme.palette.success.dark : theme.palette.success.main,
        warning: theme.palette.mode === 'dark' ? theme.palette.warning.dark : theme.palette.warning.main,
        error: theme.palette.mode === 'dark' ? theme.palette.error.dark : theme.palette.error.main,
        info: theme.palette.mode === 'dark' ? theme.palette.info.dark : theme.palette.info.main,
    }), [theme]);

    // Padding variants - memoized
    const paddingVariants = useMemo(() => ({
        none: 0,
        small: { xs: theme.spacing(2, 0), sm: theme.spacing(3, 0), md: theme.spacing(4, 0) },
        medium: { xs: theme.spacing(4, 0), sm: theme.spacing(6, 0), md: theme.spacing(8, 0) },
        large: { xs: theme.spacing(6, 0), sm: theme.spacing(8, 0), md: theme.spacing(10, 0) },
    }), [theme]);

    const getPadding = useMemo(() => {
        if (typeof padding === 'string' && paddingVariants[padding]) {
            return paddingVariants[padding];
        }
        return padding; // Custom spacing object
    }, [padding, paddingVariants]);

    // Header type styles
    const headerStyles = useMemo(() => {
        if (type === 'header') {
            return {
                marginTop: `${config.appBarHeight}px`,
                minHeight: `calc(100vh - ${config.appBarHeight}px)`,
                textAlign: 'center',
                zIndex: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            };
        }
        if (type === 'process') {
            return {
                minHeight: `calc(100vh - ${config.appBarHeight}px)`,
            };
        }
        return {
            minHeight: 'auto',
        };
    }, [type, config.appBarHeight]);

    return (
        <Box
            ref={ref}
            component={component}
            sx={{
                width: '100%',
                backgroundColor: backgroundVariants[variant] || backgroundVariants.default,
                padding: getPadding,
                display: 'flex',
                minHeight: '100vh',
                justifyContent: 'center',
                alignItems: 'center',
                ...(fullHeight && {
                    minHeight: '100vh',
                }),
                ...headerStyles,
                ...sx,
            }}
            {...props}
        >
            {children}
        </Box>
    );
}));
